import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Scale, CheckCircle, XCircle, Loader2, CreditCard } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, ready, success, error, already_paid
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [paypalReady, setPaypalReady] = useState(false);

  useEffect(() => {
    const fetchPaymentRequest = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/payment-request/token/${token}`);
        setPaymentRequest(response.data);
        setStatus('ready');
      } catch (error) {
        const detail = error.response?.data?.detail || 'Errore nel caricamento';
        setErrorMessage(detail);
        if (detail.includes('già stata pagata')) {
          setStatus('already_paid');
        } else {
          setStatus('error');
        }
      }
    };

    if (token) {
      fetchPaymentRequest();
    }
  }, [token]);

  const createPayPalOrder = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/payment-request/token/${token}/create-order`);
      return response.data.order_id;
    } catch (error) {
      toast.error('Errore nella creazione dell\'ordine PayPal');
      throw error;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/payment-request/token/${token}/capture-order?order_id=${data.orderID}`
      );
      
      if (response.data.status === 'completed') {
        setStatus('success');
        toast.success('Pagamento completato con successo!');
      }
    } catch (error) {
      toast.error('Errore nel completamento del pagamento');
    }
  };

  return (
    <PayPalScriptProvider options={{ 
      "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || 'test',
      currency: 'EUR',
      intent: 'capture',
      components: 'buttons',
      "enable-funding": 'paylater'
    }}>
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center shadow-xl border-t-4 border-t-secondary" data-testid="payment-page-card">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <Scale className="h-10 w-10 text-primary" />
            <h1 className="text-2xl font-bold text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>
              Studio Legale Nada Zhiti
            </h1>
          </div>

          {/* Loading State */}
          {status === 'loading' && (
            <div className="py-8" data-testid="loading-state">
              <Loader2 className="h-16 w-16 mx-auto mb-4 text-primary animate-spin" />
              <p className="text-lg text-gray-600">Caricamento...</p>
            </div>
          )}

          {/* Ready to Pay */}
          {status === 'ready' && paymentRequest && (
            <div className="py-4" data-testid="ready-state">
              <CreditCard className="h-16 w-16 mx-auto mb-4 text-primary" />
              <h2 className="text-2xl font-bold mb-4 text-gray-800">Richiesta di Pagamento</h2>
              
              <div className="bg-white border-2 border-gray-200 p-5 rounded-xl mb-6 shadow-sm">
                <div className="mb-4 pb-3 border-b border-gray-100">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Cliente</p>
                  <p className="font-semibold text-gray-800 text-lg">{paymentRequest.client_name}</p>
                </div>
                
                <div className="mb-4 pb-3 border-b border-gray-100">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Descrizione</p>
                  <p className="font-medium text-gray-700">{paymentRequest.description}</p>
                </div>
                
                <div className="pt-2">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Importo da pagare</p>
                  <p className="text-4xl font-bold text-primary">€{paymentRequest.amount.toFixed(2)}</p>
                </div>
              </div>

              <div className="space-y-4">
                <PayPalButtons
                  createOrder={createPayPalOrder}
                  onApprove={onPayPalApprove}
                  onInit={() => setPaypalReady(true)}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    toast.error('Errore PayPal. Riprova più tardi.');
                  }}
                  style={{ 
                    layout: 'vertical',
                    color: 'gold',
                    shape: 'rect',
                    label: 'pay'
                  }}
                />
                
                {/* Pay Later info */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 text-center">
                    <strong>💳 Paga in 3 rate</strong><br/>
                    <span className="text-blue-600">Dividi il pagamento senza interessi con PayPal</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="py-8" data-testid="success-state">
              <CheckCircle className="h-20 w-20 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold text-green-600 mb-2">Pagamento Completato!</h2>
              <p className="text-gray-600 mb-6">
                Grazie! Il pagamento è stato ricevuto con successo.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Riceverai una email di conferma.
              </p>
              <Button 
                onClick={() => navigate('/')} 
                className="bg-primary text-white hover:bg-primary/90"
              >
                Torna alla Home
              </Button>
            </div>
          )}

          {/* Already Paid State */}
          {status === 'already_paid' && (
            <div className="py-8" data-testid="already-paid-state">
              <CheckCircle className="h-20 w-20 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold text-green-600 mb-2">Già Pagato</h2>
              <p className="text-gray-600 mb-6">
                Questa richiesta di pagamento è già stata saldata.
              </p>
              <Button 
                onClick={() => navigate('/')} 
                variant="outline"
                className="border-gray-300 text-gray-700"
              >
                Torna alla Home
              </Button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="py-8" data-testid="error-state">
              <XCircle className="h-20 w-20 mx-auto mb-4 text-red-500" />
              <h2 className="text-2xl font-bold text-red-600 mb-2">Errore</h2>
              <p className="text-gray-600 mb-4">{errorMessage}</p>
              <p className="text-sm text-gray-500 mb-4">
                Per assistenza contattaci al <strong className="text-gray-700">3462393411</strong>
              </p>
              <Button 
                onClick={() => navigate('/')} 
                variant="outline"
                className="border-gray-300 text-gray-700"
              >
                Torna alla Home
              </Button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200 text-sm text-gray-500">
            <p className="font-medium text-gray-600">Studio Legale Nada Zhiti</p>
            <p>Via Roma, 24 - Casale Monferrato</p>
            <p>Tel: 3462393411 | P.IVA: 02680260029</p>
          </div>
        </Card>
      </div>
    </PayPalScriptProvider>
  );
};

export default PaymentPage;
